import type { ImportedModelResourceRevision } from './ImportedModelResourceRevision'

/**
 * 局部导入模型 revision 的退役会话。
 *
 * 该类只负责一个关键不变量：新 owner 与 WorkAssembly 快照发布后，必须先退役旧 topology，
 * 才能释放旧 GPU scope。失败计划由会话继续持有，Editor 不再拼接或丢弃清理步骤。
 */
export class ImportedModelRevisionSession {
  /** 尚未完整完成 topology 与旧 scope 退役的 revision。 */
  private readonly pendingRetirements = new Set<{
    readonly resourceRevision?: ImportedModelResourceRevision
    readonly retireTopology: () => void
    topologyRetired: boolean
  }>()

  /** 会话是否已停止接受新 revision。 */
  private disposed = false

  /**
   * 发布并尝试退役一个局部 revision。
   *
   * @param input 已准备的资源 revision、同步发布回调与 topology 清理回调。
   * @returns 此前 pending 与当前 revision 的退役错误。
   */
  public commit(input: PuppyCAD.ImportedModelRevisionCommitInput): readonly unknown[] {
    this.assertAvailable()
    const errors = this.retryPendingRetirements()
    try {
      if (input.resourceRevision) {
        input.resourceRevision.publish(input.publish)
      } else {
        input.publish()
      }
    } catch (publishError) {
      if (errors.length > 0) {
        throw new AggregateError(
          [publishError, ...errors],
          '导入模型 revision 发布失败且已有退役任务重试失败',
        )
      }
      throw publishError
    }

    const retirement = {
      ...(input.resourceRevision ? { resourceRevision: input.resourceRevision } : {}),
      retireTopology: input.retireTopology,
      topologyRetired: false,
    }
    this.pendingRetirements.add(retirement)
    this.retire(retirement, errors)
    return errors
  }

  /**
   * 重试全部 pending 退役任务。
   *
   * @returns 本次仍未完成的错误列表。
   */
  public retryPendingRetirements(): unknown[] {
    const errors: unknown[] = []
    for (const retirement of [...this.pendingRetirements]) {
      this.retire(retirement, errors)
    }
    return errors
  }

  /** 销毁会话并最后重试所有退役任务。 */
  public dispose(): void {
    this.disposed = true
    const errors = this.retryPendingRetirements()
    if (errors.length > 0) {
      throw new AggregateError(errors, '导入模型 revision 会话仍有资源退役失败')
    }
  }

  /** 按 topology、旧 scope 的固定顺序推进一个 pending 任务。 */
  private retire(
    retirement: {
      readonly resourceRevision?: ImportedModelResourceRevision
      readonly retireTopology: () => void
      topologyRetired: boolean
    },
    errors: unknown[],
  ): void {
    if (!retirement.topologyRetired) {
      try {
        retirement.retireTopology()
        retirement.topologyRetired = true
      } catch (error) {
        errors.push(error)
        return
      }
    }

    const scopeErrors = retirement.resourceRevision?.retirePreviousScopes() ?? []
    errors.push(...scopeErrors)
    if (scopeErrors.length === 0) {
      this.pendingRetirements.delete(retirement)
    }
  }

  /** dispose 后禁止继续发布新 revision。 */
  private assertAvailable(): void {
    if (this.disposed) {
      throw new Error('导入模型 revision 会话已销毁')
    }
  }
}
